/**
 * #659 §UAT-2026-07-08-R659 §R659-3 — 전역 커맨드 팔레트 검색 인덱스 유틸.
 *
 * BE·망 요청 의존이 없는 순수 함수 모음. 각 소스(공통 능력·비즈 기능·메뉴·개발표준·
 * 표준용어)의 서버 응답을 통일 `SearchHit` 로 정규화하고, 클라이언트 필터/그룹핑/상위 N
 * 절단을 담당한다. `useSearchIndex` 훅이 이 모듈을 조합해 실제 팔레트 상태를 구성한다.
 */
import GithubSlugger from 'github-slugger';
import type { ModuleNode, ConfigProperties, SpecSearchHit } from '../../common-showcase/types/showcase';
import { RECIPES, type Recipe } from '../../common-showcase/data/recipes';
import type { Pitfall } from '../../common-showcase/data/pitfalls';
import type { DecisionGuide } from '../../common-showcase/data/decision-guides';
import { guideOptionCodes } from '../../common-showcase/data/decision-guides';
import type { BizItem } from '../../_index/types/catalog';
import type { MyMenuItem } from '../../menu-management/types/menu';
import type { StandardTerm } from '../../terminology-management/types/terminology';

/**
 * §R659-2 검색 소스 5종 + queue #664 §UAT-2026-07-09-R664 §R664-5 'recipe' 1종 + queue #665
 * §UAT-2026-07-09-R665 §R665-5 'config' 1종 + queue #666 §UAT-2026-07-09-R666 §R666-5
 * 'pitfall' 1종 + queue #667 §UAT-2026-07-09-R667 §R667-5 'decision' 1종 + queue #689
 * §UAT-2026-07-14-R689 §R689-1-⑤ 'doc' 1종 + queue #693 §UAT-2026-07-14-R693 §R693-5 'spec'
 * 1종 추가(BE 전문 검색 위임 · `term` 과 동형 — 클라 재필터 0). (표준용어 = term, 금칙어는
 * 옵션이라 미포함)
 */
export type SearchSource = 'capability' | 'decision' | 'recipe' | 'config' | 'pitfall' | 'biz' | 'menu' | 'standards' | 'doc' | 'spec' | 'term';

/** 통일 검색 결과 항목 */
export interface SearchHit {
  source: SearchSource;
  /** 소스 내 고유 id (React key 겸용) */
  id: string;
  title: string;
  subtitle?: string;
  /**
   * §UAT-2026-07-13-R674 §R674-1-① — title+subtitle 을 소문자로 결합한 사전 계산 매칭
   * haystack. `build*Hits` 정규화 시점에 1회 채워, 키입력마다 재결합·재소문자화하지 않는다.
   * (없으면 `matchesQuery` 가 title/subtitle 로 즉석 조합 — 테스트 fixture 등 하위호환.)
   */
  searchText?: string;
  /** 선택 시 이동할 경로(hash 포함 가능) */
  href: string;
  /** PLANNED biz 등 — 이동 차단 대상 (dimmed) */
  disabled?: boolean;
}

/** title+subtitle 을 소문자로 결합 — `SearchHit.searchText` 사전 계산에 사용 */
function toSearchText(title: string, subtitle?: string): string {
  return `${title} ${subtitle ?? ''}`.toLowerCase();
}

export interface SearchGroup {
  source: SearchSource;
  hits: SearchHit[];
}

/**
 * 결과 그룹 표시 고정 순서 (§R659-3 wireframe 순서 · queue #664 §R664-5 'recipe' 는 'capability'
 * 다음이 자연스러워 그 자리에 편입 · queue #665 §R665-5 'config' 는 카탈로그 계열끼리 인접하도록
 * 'recipe' 다음에 편입 · queue #666 §R666-5 'pitfall' 은 카탈로그 계열 다음 · 'config' 다음에 편입 ·
 * queue #667 §R667-5 'decision' 은 카탈로그 계열 중에서도 'capability' 바로 다음에 인접 편입 ·
 * queue #689 §R689-1-⑤ 'doc' 은 'standards' 다음 · 'term' 앞에 편입(기존 9개 상대 순서 불변) ·
 * queue #693 §R693-5 'spec' 은 'doc' 다음 · 'term' 앞에 편입(기존 10개 상대 순서 불변 —
 * 문서 계열(standards→doc→spec) 마지막에 인접, 서버 위임 소스 'term' 바로 앞).
 */
export const SEARCH_GROUP_ORDER: SearchSource[] = ['capability', 'decision', 'recipe', 'config', 'pitfall', 'biz', 'menu', 'standards', 'doc', 'spec', 'term'];

/** 그룹당 상위 노출 건수 (§R659-3: "각 상위 5건") */
export const GROUP_HIT_LIMIT = 5;

/** ⑤ terminology 서버검색 최소 질의 길이 가드 (§R659-2) */
export const MIN_TERM_QUERY_LENGTH = 2;

/** ⑤ terminology 서버검색 트리거 여부 — debounce 후 이 값이 true 일 때만 fetch */
export function shouldSearchTerms(query: string): boolean {
  return query.trim().length >= MIN_TERM_QUERY_LENGTH;
}

// ─── ① 공통 능력 (Common Capability) ────────────────────────────────────────

/**
 * §UAT-2026-07-13-R674 후속(라이브 gap 확정) — 실 BE 데이터 상 다수 모듈의 title(코드+한글명)·
 * subtitle(영문명 · publicApis)만으로는 자연어 질의 토큰(예: '업로드')이 전혀 나타나지 않는
 * 경우가 있다(C009=FileService/StoredFile/FileMeta/FileValidationPolicy 뿐 · '업로드' 부재).
 * `RECIPES`(SSOT · 신규 데이터 원천 0)의 name+summary 자연어 텍스트를 소속 모듈 코드별로
 * 결합해 searchText 확장에 재사용한다. 예: 'file-pipeline' 레시피 name="파일 업로드 파이프라인"
 * → C009/C003/C014 가 '업로드' 토큰을 자동 획득. 레시피 6종 고정 데이터라 `t` 로 1회만 계산.
 */
function buildRecipeKeywordsByCode(t: (key: string) => string): Record<string, string> {
  const byCode: Record<string, string[]> = {};
  for (const recipe of RECIPES) {
    const text = `${t(recipe.nameKey)} ${t(recipe.summaryKey)}`;
    for (const m of recipe.modules) {
      (byCode[m.code] ??= []).push(text);
    }
  }
  const joined: Record<string, string> = {};
  for (const [code, texts] of Object.entries(byCode)) joined[code] = texts.join(' ');
  return joined;
}

/**
 * 레시피 파생으로도 못 채우는 소수 모듈만 소규모 큐레이션 키워드 보충(예: C078 은 어떤 레시피
 * name/summary 에도 '다운로드' 가 없어 report-export 레시피(name="리포트 · 데이터 반출")로도
 * 커버되지 않음). **등재 기준**: 실사용 자연어 질의에서 반복 recall 미스가 확인된 모듈만
 * 등재(무분별 확장 금지) — 확장 시 배열에 코드 1줄만 추가하면 된다.
 */
const MODULE_KEYWORDS: Record<string, string[]> = {
  C078: ['엑셀', '다운로드', '반출', 'xls'],
};

/**
 * `GET /api/eraf/showcase/modules` → nodes[] 정규화. 선택 시 `/showcase#<code>` (#336 계보).
 * `t` 는 §R674 후속(라이브 gap 확정) — `searchText` 확장에 쓰이는 레시피 name/summary 가
 * i18n 키라 해석이 필요하다(호출부는 `useTranslation().t`, `buildRecipeHits` 와 동형).
 */
export function buildCapabilityHits(nodes: ModuleNode[], t: (key: string) => string): SearchHit[] {
  const recipeKeywordsByCode = buildRecipeKeywordsByCode(t);
  return nodes.map((n) => {
    const title = `${n.code} ${n.nameKo}`;
    const subtitle = [n.nameEn, ...(n.publicApis ?? [])].filter(Boolean).join(' · ');
    const extraKeywords = [recipeKeywordsByCode[n.code], ...(MODULE_KEYWORDS[n.code] ?? [])].filter(Boolean).join(' ');
    return {
      source: 'capability' as const,
      id: n.code,
      title,
      subtitle,
      searchText: `${toSearchText(title, subtitle)} ${extraKeywords}`.trim().toLowerCase(),
      href: `/showcase#${n.code}`,
    };
  });
}

// ─── ①-a2 유사 능력 선택 가이드 (Decision Guide) — queue #667 §R667-5 (로컬 정적 · BE 0) ──

/**
 * `DECISION_GUIDES`(로컬 정적 비교군 데이터) → SearchHit[] 정규화. title 은 i18n 키라 `t` 로
 * 해석 필요(`buildRecipeHits`/`buildPitfallHits` 와 동형). subtitle 은 군의 전체 후보 코드를
 * 'vs' 로 결합(`guideOptionCodes`)해 코드 검색도 매칭되게 한다. 군 단위 1 hit(후보별
 * 미분할 · 노이즈 억제). 선택 시 `/showcase?view=decision&guide=<id>` → 선택 가이드 뷰 +
 * 해당 군 비교표(§R667-4 URL 동기화와 정합).
 */
export function buildDecisionHits(guides: DecisionGuide[], t: (key: string) => string): SearchHit[] {
  return guides.map((g) => {
    const title = t(g.titleKey);
    const subtitle = guideOptionCodes(g).join(' vs ');
    return {
      source: 'decision' as const,
      id: g.id,
      title,
      subtitle,
      searchText: toSearchText(title, subtitle),
      href: `/showcase?view=decision&guide=${g.id}`,
    };
  });
}

// ─── ①-b 모듈 조합 레시피 (Recipe) — queue #664 §R664-5 (로컬 정적 · BE 0) ──────────

/**
 * `RECIPES`(로컬 정적 조합 데이터) → SearchHit[] 정규화. name/summary 는 i18n 키라 `t` 로 해석
 * 필요 — 다른 build*Hits 와 달리 번역 함수를 인자로 받는다(호출부는 `useTranslation().t`).
 * 선택 시 `/showcase?view=recipes&recipe=<id>` → 레시피 뷰 + 해당 상세 딥링크(§R664-3 URL
 * 동기화와 정합).
 */
export function buildRecipeHits(recipes: Recipe[], t: (key: string) => string): SearchHit[] {
  return recipes.map((r) => {
    const title = t(r.nameKey);
    const subtitle = t(r.summaryKey);
    return {
      source: 'recipe' as const,
      id: r.id,
      title,
      subtitle,
      searchText: toSearchText(title, subtitle),
      href: `/showcase?view=recipes&recipe=${r.id}`,
    };
  });
}

// ─── ①-c 설정 속성 (Config Reference) — queue #665 §R665-5 (로컬 정적 · BE 0) ───────

/**
 * `data/config-properties.json`(빌드타임 생성 · apps/web/scripts/extract-config-metadata.mjs)
 * → SearchHit[] 정규화. 대량 hit(수백건)이므로 검색어가 있을 때만 노출되도록 `filterHits` 가
 * 빈 질의에서 빈 배열을 반환하는 기존 동작을 그대로 재사용한다(빈 검색어 전체 나열 억제).
 * 선택 시 `/showcase?artifact=<a>#config-<name>` — 해당 모듈 상세 '설정' 섹션 딥링크
 * (§R665-3 렌더가 앵커 id 를 부여).
 */
export function buildConfigHits(data: ConfigProperties): SearchHit[] {
  const hits: SearchHit[] = [];
  for (const [artifact, mod] of Object.entries(data.modules ?? {})) {
    for (const p of mod.properties) {
      const title = p.name;
      const subtitle = artifact;
      hits.push({
        source: 'config' as const,
        id: p.name,
        title,
        subtitle,
        searchText: toSearchText(title, subtitle),
        href: `/showcase?artifact=${artifact}#config-${p.name}`,
      });
    }
  }
  return hits;
}

// ─── ①-d 알려진 함정 (Pitfall) — queue #666 §R666-5 (로컬 정적 · BE 0) ────────────

/**
 * `PITFALLS`(로컬 정적 큐레이션 데이터) → SearchHit[] 정규화. title 은 i18n 키라 `t` 로
 * 해석 필요(`buildRecipeHits` 와 동형). 선택 시 `/showcase?view=pitfalls&pitfall=<id>` →
 * 함정 사전 뷰 + 해당 상세 딥링크(§R666-4 URL 동기화와 정합).
 */
export function buildPitfallHits(pitfalls: Pitfall[], t: (key: string) => string): SearchHit[] {
  return pitfalls.map((p) => {
    const title = t(p.titleKey);
    const subtitle = p.moduleCodes.join(' · ');
    return {
      source: 'pitfall' as const,
      id: p.id,
      title,
      subtitle,
      searchText: toSearchText(title, subtitle),
      href: `/showcase?view=pitfalls&pitfall=${p.id}`,
    };
  });
}

// ─── ② 비즈 기능 (Biz Feature) ──────────────────────────────────────────────

/**
 * `GET /api/eraf/biz/catalog` → biz[] 정규화. 선택 시 `<biz.url>` 이동.
 * status=PLANNED 은 dimmed·이동 차단 (#316/#R10-U-002-C 계보).
 */
export function buildBizHits(biz: BizItem[]): SearchHit[] {
  return biz.map((b) => ({
    source: 'biz' as const,
    id: b.feature,
    title: b.nameKo,
    subtitle: b.url,
    searchText: toSearchText(b.nameKo, b.url),
    href: b.url,
    disabled: b.status === 'PLANNED',
  }));
}

// ─── ③ 사이드바 메뉴 (Menu) ─────────────────────────────────────────────────

/** DIVIDER·url 없는 노드를 제외하고 트리를 평탄화 */
function flattenMenus(items: MyMenuItem[]): MyMenuItem[] {
  const out: MyMenuItem[] = [];
  for (const item of items) {
    if (item.menuType !== 'DIVIDER' && item.url) out.push(item);
    if (item.children && item.children.length > 0) out.push(...flattenMenus(item.children));
  }
  return out;
}

/** `GET /api/eraf/menus/me`(역할 필터 자동) → 평탄화된 MyMenuItem[] 정규화 */
export function buildMenuHits(menus: MyMenuItem[]): SearchHit[] {
  return flattenMenus(menus).map((m) => ({
    source: 'menu' as const,
    id: m.menuKey,
    title: m.nameKo,
    subtitle: m.url ?? undefined,
    searchText: toSearchText(m.nameKo, m.url ?? undefined),
    href: m.url ?? '',
  }));
}

// ─── ④ 개발표준 섹션 (Development Standards) ───────────────────────────────

export interface StandardsHeading {
  level: number;
  text: string;
  slug: string;
  /**
   * §UAT-2026-07-14-R689 §R689-1-③ — 헤딩이 위치한 0-based 라인 번호. `splitMarkdownSections`
   * 가 이 값으로 [heading.line+1, 다음 h1~h3 헤딩.line) 구간을 본문으로 잘라낸다. 기존
   * 소비자(`buildStandardsHits`/`StandardsToc`)는 이 필드를 참조하지 않아 무영향.
   */
  line: number;
}

/** 헤딩 원문에서 흔한 인라인 마크다운 문법을 제거 — 렌더된 plain text 와 slugger 입력을 정합 */
function stripMarkdownInline(text: string): string {
  return text
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/_([^_]+)_/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .trim();
}

const HEADING_RE = /^(#{1,6})\s+(.+?)\s*#*$/;

/**
 * markdownText 전체를 ATX 헤딩(#~######) 순서대로 훑어 rehype-slug(github-slugger) 와
 * 동일한 순번으로 슬러그를 부여한다. 반환은 전체 레벨(h1~h6) — 호출자가 h2/h3 만 필터링.
 * (rehype-slug 는 문서 내 모든 헤딩을 대상으로 유일성 카운터를 증가시키므로, h2/h3 만
 * 슬러거에 넣으면 실제 렌더 결과와 슬러그가 어긋난다 — #659 §R659-2-④ 슬러그 정합 요구.)
 */
export function parseStandardsHeadings(markdownText: string): StandardsHeading[] {
  const slugger = new GithubSlugger();
  const headings: StandardsHeading[] = [];
  let inFence = false;

  const rawLines = markdownText.split('\n');
  for (let lineNo = 0; lineNo < rawLines.length; lineNo++) {
    const line = rawLines[lineNo].trim();
    if (/^```/.test(line)) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;

    const m = HEADING_RE.exec(line);
    if (!m) continue;
    const level = m[1].length;
    const text = stripMarkdownInline(m[2]);
    if (!text) continue;
    headings.push({ level, text, slug: slugger.slug(text), line: lineNo });
  }

  return headings;
}

/**
 * §UAT-2026-07-14-R689 §R689-1-① h2/h3 섹션 1건 — 본문은 그 헤딩 다음 줄부터 다음
 * h1~h3 헤딩 직전까지(그 사이 h4~h6 는 포함). `buildStandardsHits`/`buildDocHits` 공용.
 */
export interface MarkdownSection {
  level: number;
  text: string;
  slug: string;
  /** 헤딩 다음 줄부터 다음 h1~h3 헤딩 직전까지의 원문(정규화 전) */
  body: string;
}

/**
 * §R689-1-③ 파서 SSOT 1곳 — `parseStandardsHeadings` 의 라인 인덱스로 h2/h3 섹션 경계를
 * 잘라낸다. 슬러그가 빈 문자열인 섹션(헤딩이 기호뿐)은 깨진 딥링크를 막기 위해 제외한다
 * (§R689-3 구현 주의 5).
 */
export function splitMarkdownSections(markdownText: string): MarkdownSection[] {
  const headings = parseStandardsHeadings(markdownText);
  const lines = markdownText.split('\n');
  const sections: MarkdownSection[] = [];

  for (let i = 0; i < headings.length; i++) {
    const heading = headings[i];
    if ((heading.level !== 2 && heading.level !== 3) || !heading.slug) continue;

    let endLine = lines.length;
    for (let j = i + 1; j < headings.length; j++) {
      if (headings[j].level <= 3) {
        endLine = headings[j].line;
        break;
      }
    }

    sections.push({
      level: heading.level,
      text: heading.text,
      slug: heading.slug,
      body: lines.slice(heading.line + 1, endLine).join('\n'),
    });
  }

  return sections;
}

/** §R689-1-④ 본문 발췌 상한 — 36섹션 × 2언어 ≈ 17K자(팔레트 오픈 1회 계산). */
export const EXCERPT_MAX_CHARS = 240;

/**
 * 코드펜스(```) 블록 전체 제거 — 명령어 나열이 자연어 recall 을 오염시키지 않도록. 인라인
 * 코드(단일 백틱)는 여기서 건드리지 않고 `stripMarkdownInline` 이 태그만 벗겨 내용을 보존한다
 * (§R689-3 구현 주의 4 — `sparse-checkout` 같은 식별자는 본문 산문에 남아야 매칭된다).
 */
function stripCodeFences(text: string): string {
  const out: string[] = [];
  let inFence = false;
  for (const rawLine of text.split('\n')) {
    if (/^```/.test(rawLine.trim())) {
      inFence = !inFence;
      continue;
    }
    if (inFence) continue;
    out.push(rawLine);
  }
  return out.join('\n');
}

/**
 * §R689-1-④ 섹션 본문 원문 → 검색용 정규화 발췌. 코드펜스 제거 → 줄 단위
 * `stripMarkdownInline`(표/링크/강조 기호 제거 · 인라인 코드는 내용 보존) → 공백 1칸 축약 →
 * 선두 `EXCERPT_MAX_CHARS` 자 절단.
 */
export function normalizeSectionBody(raw: string): string {
  const withoutFences = stripCodeFences(raw);
  const plain = withoutFences
    .split('\n')
    .map((line) => stripMarkdownInline(line))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  return plain.slice(0, EXCERPT_MAX_CHARS);
}

/**
 * `GET /api/eraf/standards/active` → markdownText H2/H3 파싱. 선택 시 `/standards#<slug>`.
 * queue #689 §UAT-2026-07-14-R689 §R689-1-⑤ — 그룹/딥링크는 불변, `searchText` 에만 본문
 * 발췌(`normalizeSectionBody`)를 더해 recall 을 확장한다(제목만 인덱싱하던 결함 정정).
 */
export function buildStandardsHits(markdownText: string): SearchHit[] {
  return splitMarkdownSections(markdownText).map((section) => {
    const excerpt = normalizeSectionBody(section.body);
    return {
      source: 'standards' as const,
      id: section.slug,
      title: section.text,
      searchText: `${toSearchText(section.text)} ${excerpt}`.trim().toLowerCase(),
      href: `/standards#${section.slug}`,
    };
  });
}

// ─── ④-a 문서 본문 섹션 (Doc) — queue #689 §UAT-2026-07-14-R689 §R689-1 (로컬 인메모리 · BE 0) ──

export interface DocSource {
  /** 결과 subtitle 에 노출할 문서명(예: '소비자 가이드' / 'Consumer Guide') */
  docTitle: string;
  /** 원문 마크다운(빌드타임 번들 · 팔레트 오픈 시 동적 import) */
  markdownText: string;
  /** 해시를 제외한 딥링크 베이스 경로(예: `/getting-started/consumer-guide`) */
  hrefBase: string;
}

/**
 * 문서(현재: 소비자 가이드 KO/EN)를 h2/h3 섹션 단위로 분해해 SearchHit[] 로 정규화한다.
 * `title` = 섹션명(헤딩) · `subtitle` = 문서명 + 본문 발췌 앞 80자(§R689-1-④) · `searchText` =
 * 섹션명 + 문서명 + 본문 발췌(소문자) · `href` = `${hrefBase}#${slug}`. 신규 prebuild 산출물
 * 없이 기존 번들 JSON 을 그대로 받는다(§R689-1-② — 신규 배포 자산 0).
 */
export function buildDocHits(source: DocSource): SearchHit[] {
  const { docTitle, markdownText, hrefBase } = source;
  return splitMarkdownSections(markdownText).map((section) => {
    const excerpt = normalizeSectionBody(section.body);
    const href = `${hrefBase}#${section.slug}`;
    const subtitle = excerpt ? `${docTitle} — ${excerpt.slice(0, 80)}` : docTitle;
    return {
      source: 'doc' as const,
      id: href,
      title: section.text,
      subtitle,
      searchText: `${toSearchText(section.text, docTitle)} ${excerpt}`.trim().toLowerCase(),
      href,
    };
  });
}

// ─── ⑤ 표준용어 (Standard Term) ─────────────────────────────────────────────

/**
 * `GET /api/eraf/terminology/standard-terms?keyword=` 응답 content[] 정규화.
 * 서버가 이미 pg_trgm 유사도로 필터/정렬했으므로 클라이언트 재필터 없이 그대로 매핑.
 * 선택 시 `/admin/terminology/standard-terms/:id`.
 */
export function buildTermHits(terms: StandardTerm[]): SearchHit[] {
  return terms.map((term) => ({
    source: 'term' as const,
    id: String(term.id),
    title: `${term.korean} (${term.englishShort})`,
    subtitle: term.description ?? undefined,
    href: `/admin/terminology/standard-terms/${term.id}`,
  }));
}

// ─── ⑤-a 모듈 스펙 본문 (Spec) — queue #693 §UAT-2026-07-14-R693 §R693-5 (BE 전문 검색 · 서버 필터) ──

/**
 * `GET /api/eraf/catalog/specs?q=` 응답 hits[] 정규화. BE 가 이미 토큰 AND + 스코어로 필터/정렬
 * 했으므로(§R693-4) `filterHits` 를 적용하지 않는다 — `buildTermHits` 와 동형(클라 재필터 0).
 * 선택 시 `/showcase?spec=<sectionSlug>#<code>` → 노드 선택(#679 퍼머링크) + 스펙 모달 자동
 * 오픈 + 앵커 스크롤(§R693-5).
 */
export function buildSpecHits(hits: SpecSearchHit[]): SearchHit[] {
  return hits.map((hit) => ({
    source: 'spec' as const,
    id: `${hit.code}#${hit.sectionSlug}`,
    title: hit.sectionTitle,
    subtitle: `${hit.code} · ${hit.specTitle} — ${hit.excerpt.slice(0, 80)}`,
    href: `/showcase?spec=${hit.sectionSlug}#${hit.code}`,
  }));
}

// ─── 필터 · 그룹핑 (①②③④ 클라이언트 인메모리 필터) ─────────────────────────

/**
 * §UAT-2026-07-13-R674 §R674-1-② ko↔en 시노님 소사전(15군). 그룹 배열이라 신규 쌍 추가 =
 * 배열 1줄. 사전은 소문자 키·소문자 값 — 토큰 매칭 시 각 토큰을 자기 자신 + 동의 그룹 전체로
 * 확장해, 확장군 중 하나라도 haystack 에 포함되면 그 토큰은 충족(그룹 내 OR · 토큰 간 AND).
 */
const SEARCH_SYNONYMS: string[][] = [
  ['엑셀', 'excel', 'xls', 'xlsx', '스프레드시트'],
  ['업로드', 'upload', '올리기', '첨부', 'attach'],
  ['다운로드', 'download', '내보내기', 'export', '반출'],
  ['인증', 'auth', 'jwt', '로그인', 'login'],
  ['파일', 'file', 'storage'],
  ['알림', 'notification', 'notify'],
  ['검증', 'validation', 'valid'],
  ['캐시', 'cache', 'redis'],
  ['권한', 'permission', 'authorization', 'rbac'],
  ['이벤트', 'event', 'messaging', 'kafka'],
  ['감사', 'audit', 'auditlog'],
  ['암호화', 'encrypt', 'crypto'],
  ['조회', 'search', 'query'],
  ['오류', 'error', 'exception'],
  ['망', 'network', 'gateway'],
  // ─── queue #683 §UAT-2026-07-14-R683 §R683-1-① 행위 동사 시노님 신규 군 ───
  ['발송', '보내기', '전송', '발신', 'send', 'sender', 'dispatch'],
  ['이메일', '메일', 'email', 'mail'],
  ['생성', '만들기', 'create', 'new', '등록'],
  ['삭제', '지우기', '제거', 'delete', 'remove'],
];

/** 시노님 역방향 lookup — 모듈 로드 시 1회 빌드(§R674-3-1: 키입력마다 재계산 금지) */
const SYNONYM_LOOKUP: Record<string, string[]> = (() => {
  const map: Record<string, string[]> = {};
  for (const group of SEARCH_SYNONYMS) {
    for (const word of group) map[word] = group;
  }
  return map;
})();

/** 토큰을 자기 자신 + 동의 그룹 전체로 확장 (매핑 없으면 자기 자신 1개 배열) */
function expandToken(token: string): string[] {
  return SYNONYM_LOOKUP[token] ?? [token];
}

/**
 * queue #683 §UAT-2026-07-14-R683 §R683-1-② 검색 의도와 무관한 필러 토큰(모두 소문자 비교).
 * `stripStopwords` 규칙 A/B 가드와 함께만 사용 — 이 Set 자체는 정적 상수(조회 O(1)).
 */
const SEARCH_STOPWORDS = new Set<string>(['구현', '방법', '하는법', '기능', '사용', '개발', '처리']);

/**
 * §R683-1-② 필러 스톱워드 제거 — ★모듈명 구성 토큰 충돌 회피 규칙:
 * 규칙 A(전체 스톱워드 가드) — 토큰 전체가 스톱워드면 제거를 적용하지 않고 원본 유지
 * (예: `'처리'` 단독 검색이 `Excel 처리`·`PDF 처리` 를 계속 매칭하도록).
 * 규칙 B(병행 시에만 제외) — 비-스톱워드 토큰이 1개 이상 남을 때만 스톱워드를 제외
 * (예: `'엑셀 다운로드 구현'` → `구현` 제외 · `'excel 처리'` → `처리` 제외).
 */
function stripStopwords(tokens: string[]): string[] {
  const nonStopwords = tokens.filter((token) => !SEARCH_STOPWORDS.has(token));
  return nonStopwords.length > 0 ? nonStopwords : tokens;
}

/** 질의를 공백 기준 소문자 토큰 배열로 분해 → 필러 스톱워드 제거 — 빈 토큰 제거 */
function tokenizeQuery(query: string): string[] {
  const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return stripStopwords(tokens);
}

/** hit 의 매칭 haystack — 사전 계산 `searchText` 우선, 없으면 즉석 조합(테스트 fixture 하위호환) */
function hitHaystack(hit: SearchHit): string {
  return hit.searchText ?? toSearchText(hit.title, hit.subtitle);
}

/**
 * 질의를 공백 토큰화 → 토큰별 시노님 확장 → **전 토큰 AND**(각 토큰은 확장군 내 OR)로
 * haystack(title+subtitle 결합)에 매칭한다. 단일 토큰 질의는 기존 substring 동작과 동치.
 */
export function matchesQuery(hit: SearchHit, query: string): boolean {
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) return false;
  const haystack = hitHaystack(hit);
  return tokens.every((token) => expandToken(token).some((word) => haystack.includes(word)));
}

/**
 * §R674-1-③ 매칭 스코어: 코드 정확 일치(100) > 이름(title) 전 토큰 일치(50) > 설명(subtitle)
 * 매치(20). `filterHits` 가 이미 매칭된 hit 에 한해 순위만 매기므로, 마지막 분기(20)는
 * "제목만으론 전 토큰이 충족되지 않아 subtitle 도움으로 매칭됨"을 의미한다.
 */
function computeMatchScore(hit: SearchHit, tokens: string[]): number {
  const idLower = hit.id.toLowerCase();
  if (tokens.some((token) => idLower === token)) return 100;

  const titleLower = hit.title.toLowerCase();
  const allTokensInTitle = tokens.every((token) => expandToken(token).some((word) => titleLower.includes(word)));
  if (allTokensInTitle) return 50;

  return 20;
}

/** §R683-1-③ OR-폴백 1차 정렬 키 — 질의 토큰 중 확장군에 하나라도 매칭된 서로 다른 토큰 수 */
function countMatchedTokens(hit: SearchHit, tokens: string[]): number {
  const haystack = hitHaystack(hit);
  return tokens.filter((token) => expandToken(token).some((word) => haystack.includes(word))).length;
}

/**
 * §R683-1-③ OR-폴백 2차 정렬 키 — 필드 가중(명칭 title=3 > 클래스명/영문명 subtitle=2 >
 * 설명 확장부 searchText 전용=1). 토큰이 title 에서 매칭되면 그 hit 은 3, 아니면 subtitle 을
 * 확인해 2, 그마저 아니면(확장 searchText 부분에서만 매칭) 1을 부여한다.
 */
function computeFieldWeight(hit: SearchHit, tokens: string[]): number {
  const matchesField = (text: string) => tokens.some((token) => expandToken(token).some((word) => text.includes(word)));
  if (matchesField(hit.title.toLowerCase())) return 3;
  if (hit.subtitle && matchesField(hit.subtitle.toLowerCase())) return 2;
  return 1;
}

/**
 * ①②③④ 로컬 인덱스에 대해 클라이언트 필터 적용 (⑤ term 은 서버가 이미 필터).
 * 매칭된 hit 을 §R674-1-③ 스코어 내림차순으로 정렬 — `groupHits` 는 소스별로 순서를 보존한
 * 채 상위 N 만 절단하므로, 여기서 정렬해두면 그룹 내부 정렬 결과가 그대로 유지된다.
 *
 * queue #683 §UAT-2026-07-14-R683 §R683-1-③ — AND 매칭이 0건이면(카테고리 = 이 함수가 받는
 * 단일 소스 배열 경계 안에서) OR 로 재평가한다. 정밀도 가드로 **과반 매칭**(매칭 토큰 수가
 * 전체 질의 토큰 수의 과반 초과)한 hit 만 채택 — 딱 1/2 매칭(예: 2토큰 중 1개만 매칭)은
 * §R674 무회귀 대상('파일 존재하지않는토큰' 반증 fixture)이라 제외한다. AND 결과가 1건이라도
 * 있으면 폴백은 전혀 돌지 않는다(정밀도 우선).
 */
export function filterHits(hits: SearchHit[], query: string): SearchHit[] {
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) return [];

  const andMatches = hits.filter((hit) => matchesQuery(hit, query));
  if (andMatches.length > 0) {
    return andMatches
      .map((hit) => ({ hit, score: computeMatchScore(hit, tokens) }))
      .sort((a, b) => b.score - a.score)
      .map((entry) => entry.hit);
  }

  return hits
    .map((hit) => ({ hit, matchedCount: countMatchedTokens(hit, tokens) }))
    .filter((entry) => entry.matchedCount * 2 > tokens.length)
    .map((entry) => ({ ...entry, fieldWeight: computeFieldWeight(entry.hit, tokens) }))
    .sort((a, b) => b.matchedCount - a.matchedCount || b.fieldWeight - a.fieldWeight)
    .map((entry) => entry.hit);
}

/** 5 소스 고정 순서로 그룹핑 + 그룹당 상위 N 절단. 결과가 없는 그룹은 생략. */
export function groupHits(hits: SearchHit[], limitPerGroup: number = GROUP_HIT_LIMIT): SearchGroup[] {
  return SEARCH_GROUP_ORDER
    .map((source) => ({
      source,
      hits: hits.filter((hit) => hit.source === source).slice(0, limitPerGroup),
    }))
    .filter((group) => group.hits.length > 0);
}
